import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth, authErrorMessage } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { checkPassword } from "../lib/password";
import { LEGAL } from "../lib/legal";
import { Modal } from "./ui";
import { useI18n, useT } from "../i18n";
import { withLang } from "../i18n/paths";
import Pic from "./pics";

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  );
}

// <terms>…</terms> and <privacy>…</privacy> links for translated sentences.
function useLegalLinks() {
  const { lang } = useI18n();
  return {
    terms: (c, k) => (
      <Link key={k} to={withLang(lang, "/terms")} target="_blank">
        {c}
      </Link>
    ),
    privacy: (c, k) => (
      <Link key={k} to={withLang(lang, "/privacy")} target="_blank">
        {c}
      </Link>
    ),
  };
}

// New accounts must accept the Terms and confirm their age. `nudge`
// highlights the box when someone tries Google sign-up without ticking it.
function Agreement({ checked, onChange, nudge, boxRef }) {
  const { t, tx } = useI18n();
  const links = useLegalLinks();
  const show = nudge && !checked;
  return (
    <div className={`agree-wrap ${show ? "nudge" : ""}`}>
      <label className="agree-check">
        <input ref={boxRef} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span>
          {tx(
            "I'm at least {age} (or old enough in my country) and I agree to the <terms>Terms of Service</terms> and <privacy>Privacy Policy</privacy>.",
            { age: LEGAL.minAge },
            links
          )}
        </span>
      </label>
      {show && <p className="agree-hint">{t("Tick this box first to continue.")}</p>}
    </div>
  );
}

export function LegalNotice({ action = "continuing" }) {
  const { tx } = useI18n();
  const links = useLegalLinks();
  const vars = { age: LEGAL.minAge };
  return (
    <p className="legal-note center">
      {action === "playing"
        ? tx(
            "By playing you agree to the <terms>Terms of Service</terms> and <privacy>Privacy Policy</privacy> and confirm you're at least {age} (or old enough in your country).",
            vars,
            links
          )
        : tx(
            "By continuing you agree to the <terms>Terms of Service</terms> and <privacy>Privacy Policy</privacy> and confirm you're at least {age} (or old enough in your country).",
            vars,
            links
          )}
    </p>
  );
}

export function PasswordChecklist({ password }) {
  const t = useT();
  const { results } = checkPassword(password);
  return (
    <ul className="pw-checklist">
      {results.map((r) => (
        <li key={r.id} className={r.ok ? "ok" : ""}>
          <Pic name={r.ok ? "check" : "box"} /> {t(r.label)}
        </li>
      ))}
    </ul>
  );
}

export default function AuthModal() {
  const { authModal, closeAuth, openAuth, signInGoogle, signUpEmail, signInEmail, resetPassword, user, isGuest } =
    useAuth();
  const { toast } = useToast();
  const t = useT();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [nudge, setNudge] = useState(false);
  const agreeRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const mode = authModal;
  const isSignup = mode === "signup";

  useEffect(() => {
    setError("");
    setPassword("");
    setConfirm("");
    setNudge(false);
  }, [mode]);

  // Close automatically once a real account is signed in.
  useEffect(() => {
    if (mode && user && !user.isAnonymous) closeAuth();
  }, [mode, user, closeAuth]);

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (e) {
      setError(authErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const pwValid = checkPassword(password).valid;
  const canSubmit = isSignup
    ? name.trim().length >= 2 && email && pwValid && password === confirm && agreed
    : email && password;

  const submit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    run(() => (isSignup ? signUpEmail(name, email, password) : signInEmail(email, password)));
  };

  // Signing up with Google needs the box at the bottom ticked first.
  const google = () => {
    if (isSignup && !agreed) {
      setNudge(true);
      agreeRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      agreeRef.current?.focus({ preventScroll: true });
      return;
    }
    run(signInGoogle);
  };

  const forgot = () => {
    if (!email) return setError(t("Type your email above first, then hit “Forgot password”."));
    run(async () => {
      await resetPassword(email);
      toast({ emoji: "mail", title: t("Reset link sent"), body: t("Check {email}", { email }) });
    });
  };

  return (
    <Modal open={!!mode} onClose={closeAuth} className="auth-modal" title={isSignup ? t("Create account") : t("Sign in")}>
      <div className="auth-header">
        <div className="auth-cards" aria-hidden>
          <span><Pic name="cards" /></span>
          <span><Pic name="palette" /></span>
          <span><Pic name="dice" /></span>
        </div>
        <h2>{isSignup ? t("Join the party") : t("Welcome back!")}</h2>
        <p className="muted">
          {isGuest
            ? t("Save your XP, get a friend code, and add your crew. You'll keep your seat in any room.")
            : isSignup
            ? t("Get a friend code, earn XP and invite your crew.")
            : t("Sign in to see your friends and level.")}
        </p>
      </div>

      <button className="btn btn-google btn-block" onClick={google} disabled={busy}>
        <GoogleIcon /> {t("Continue with Google")}
      </button>
      {!isSignup && <LegalNotice />}

      <div className="divider">
        <span>{t("or use email")}</span>
      </div>

      <form onSubmit={submit} className="auth-form">
        {isSignup && (
          <label>
            {t("Display name")}
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={20} placeholder={t("Captain Doodle")} />
          </label>
        )}
        <label>
          {t("Email")}
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
        </label>
        <label>
          {t("Password")}
          <div className="pw-field">
            <input
              type={showPw ? "text" : "password"}
              value={password}
              maxLength={isSignup ? 20 : 128}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={isSignup ? "new-password" : "current-password"}
            />
            <button
              type="button"
              className="pw-toggle"
              onClick={() => setShowPw((s) => !s)}
              aria-label={showPw ? t("Hide password") : t("Show password")}
            >
              <Pic name={showPw ? "eyeOff" : "eye"} />
            </button>
          </div>
        </label>
        {!isSignup && (
          <button type="button" className="link forgot-link" onClick={forgot} disabled={busy}>
            {t("Forgot password?")}
          </button>
        )}
        {isSignup && (
          <>
            <PasswordChecklist password={password} />
            <label>
              {t("Confirm password")}
              <input
                type={showPw ? "text" : "password"}
                value={confirm}
                maxLength={20}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
              />
            </label>
            {confirm && confirm !== password && <p className="field-error">{t("Passwords don't match.")}</p>}
            <Agreement checked={agreed} onChange={setAgreed} nudge={nudge} boxRef={agreeRef} />
          </>
        )}
        {error && <p className="form-error">{error}</p>}
        <button className="btn btn-primary btn-block btn-lg" disabled={!canSubmit || busy}>
          {busy ? t("One sec…") : isSignup ? t("Create account") : t("Sign in")}
        </button>
        {isSignup && <p className="legal-note center">{t("We'll email you a link to confirm it's really you.")}</p>}
      </form>

      <div className="auth-footer">
        {isSignup ? (
          <>
            <span>
              {t("Already have an account?")}{" "}
              <button className="link" onClick={() => openAuth("signin")}>
                {t("Sign in")}
              </button>
            </span>
            <button className="link" onClick={forgot} disabled={busy}>
              {t("Forgot password?")}
            </button>
          </>
        ) : (
          <span>
            {t("New here?")}{" "}
            <button className="link" onClick={() => openAuth("signup")}>
              {t("Create an account")}
            </button>
          </span>
        )}
      </div>
    </Modal>
  );
}
